import type { VNode } from 'vue'
import * as store from './toast-store'
import type { PromiseData, ToastOptions, ToastRecord, ToastType } from './toast-types'

type ToastId = number | string

function resolveMessage<T>(value: string | ((arg: T) => string), arg: T): string {
  return typeof value === 'function' ? value(arg) : value
}

function base(message: string, opts?: ToastOptions): ToastId {
  return store.create({ ...opts, title: message, type: opts?.type ?? 'default' })
}

function typed(type: ToastType) {
  return (message: string, opts?: ToastOptions): ToastId =>
    store.create({ ...opts, title: message, type })
}

const success = typed('success')
const error = typed('error')
const warning = typed('warning')
const info = typed('info')

function loading(message: string, opts?: ToastOptions): ToastId {
  return store.create({
    ...opts,
    title: message,
    type: 'loading',
    duration: opts?.duration ?? Infinity,
  })
}

function custom(render: (toast: ToastRecord) => VNode, opts?: ToastOptions): ToastId {
  return store.create({
    ...opts,
    title: opts?.description ?? '',
    type: opts?.type ?? 'default',
    render,
  })
}

interface PromiseResult<T> {
  unwrap: () => Promise<T>
}

function promise<T>(
  promise: Promise<T> | (() => Promise<T>),
  data: PromiseData<T>,
): PromiseResult<T> {
  const id = store.create({
    id: data.id,
    title: data.loading,
    description: data.description,
    type: 'loading',
    duration: Infinity,
  })

  const source = Promise.resolve(typeof promise === 'function' ? promise() : promise)

  source.then(
    (value) => {
      store.create({
        id,
        title: resolveMessage(data.success, value),
        type: 'success',
        duration: undefined,
      })
    },
    (reason: unknown) => {
      store.create({
        id,
        title: resolveMessage(data.error, reason),
        type: 'error',
        duration: undefined,
      })
    },
  )

  return { unwrap: () => source }
}

/** The public toast API: a callable with every helper attached. */
export const toast = Object.assign(base, {
  success,
  error,
  warning,
  info,
  loading,
  message: base,
  custom,
  promise,
  dismiss: store.dismiss,
})
